import { SectionShell } from "@/components/design-system/SectionShell";
import { FONTS } from "@/components/design-system/palette";

const code = `// Base layer (design-system.css) — bare elements are already styled:
//   body  Montserrat 300, 16px / 1.7
//   h1    Cormorant Garamond 300, clamp(2.9rem, 5.6vw, 4.9rem) / 1.04  (2.55rem < 720px)
//   h2    Cormorant Garamond 300, clamp(2.1rem, 3.6vw, 3.15rem) / 1.12 (2rem < 720px)
//   h3    Cormorant Garamond 300, 1.5rem / 1.25
//   headings: letter-spacing .005em

<div className="eyebrow">Occasions</div>
<h2>Every bottle belongs to a moment</h2>
<p className="lede">Tell us the occasion and we will design a label that carries it.</p>

<Link href="/" className="wordmark">SOH GD <em>Wines</em></Link>
<Link href="/wines" className="nav-link">Our Wines</Link>
<Link href="/faqs" className="footer-link">FAQs</Link>`;

export function TypographySection() {
  return (
    <SectionShell
      id="typography"
      title="Typography"
      description={
        <>
          Two families. Headlines use <code>{FONTS.display}</code> at weight
          300; body copy uses <code>{FONTS.body}</code> Light at 16px / 1.7.
          Both load from Google Fonts via <code>&lt;link&gt;</code> tags in the
          app's HTML <code>&lt;head&gt;</code>. The public site adds a few
          component classes for its repeated type treatments:{" "}
          <code>.eyebrow</code>, <code>.lede</code>, <code>.wordmark</code>,{" "}
          <code>.nav-link</code> and <code>.footer-link</code>.
        </>
      }
      whenToUse={
        <ul>
          <li>
            Write bare <code>&lt;h1&gt;</code>–<code>&lt;h3&gt;</code> and{" "}
            <code>&lt;p&gt;</code>; the base layer sizes them.
          </li>
          <li>
            <code>.eyebrow</code> above a section heading (add{" "}
            <code>.eyebrow-center</code> for centred heads);{" "}
            <code>.lede</code> for the intro paragraph under it.
          </li>
          <li>
            Wrap a phrase of a hero <code>&lt;h1&gt;</code> in{" "}
            <code>&lt;span className="soft"&gt;</code> for the italic gold
            accent.
          </li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>
            Don't import additional font families — propose adding to the
            system instead.
          </li>
          <li>
            Don't add size / weight utilities to headings or paragraphs —
            extend the base layer or add a component class.
          </li>
        </ul>
      }
      preview={
        <div className="space-y-8">
          <div>
            <div className="eyebrow">Occasions</div>
            <h1>
              Premium South African wine.{" "}
              <span className="soft italic text-accent-display">
                Your brand on the bottle.
              </span>
            </h1>
          </div>
          <div>
            <h2>Every bottle belongs to a moment</h2>
            <p className="lede mt-5">
              Tell us the occasion and we will design a label that carries it
              — from a boardroom gift to a wedding table to the last day of a
              season.
            </p>
          </div>
          <div>
            <h3>Choose your wine</h3>
            <p className="mt-2">
              Body copy in Montserrat Light. We guide you through the range and
              send samples.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-8">
            <span className="wordmark">
              SOH GD <em>Wines</em>
            </span>
            <a href="#typography" className="nav-link">
              Our Wines
            </a>
            <a href="#typography" className="footer-link">
              FAQs
            </a>
          </div>
        </div>
      }
      code={code}
    />
  );
}
